import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Stage } from 'react-konva'
import {
  EDITOR_CONTEXT_MENU_STORE,
  EDITOR_INTERACTIVE_AREAS_STORE,
  EDITOR_MONITOR_STORE,
  EDITOR_TIMELINE_STORE,
  getCursorPosition, isRightBttnClicked
} from 'store'
import ContextMenu from './ContextMenu'
import styles from './InteractiveAreaCanvas.module.scss'
import Rectangle from './Rectangle'
import { InteractiveAreaCanvasProps } from './types'
import { areRectsOverlapping, clickedOnEmpty, getStageDimensions, validateRect } from './utils'
import { Rect } from '../../../../../models'

let isMouseDown = false
const InteractiveAreaCanvas = (props: InteractiveAreaCanvasProps) => {
  const {
    [EDITOR_MONITOR_STORE]: {
      createIA,
      rectanglesForCurrentFrame,
      changeRectangle,
      setStageDimensions,
      stageWidth,
      stageHeight
    },
    [EDITOR_TIMELINE_STORE]: {
      currentMonitorFrame
    },
    [EDITOR_CONTEXT_MENU_STORE]: {
      onRectangleSelect,
      contextMenu,
      clearContextMenu,
      onStageRightClick
    },
    [EDITOR_INTERACTIVE_AREAS_STORE]: {
      isIaSelected,
      setSelectedInteractiveAreaIds
    },
    containerWidth,
    containerHeight
  } = props

  const [rectangle, setRectangle] = useState(null)
  const [mounted, setMountedFlag] = useState(false)
  const containerRef: any = useRef()
  const stageRef: any = useRef()

  useEffect(() => setMountedFlag(true), [])

  useEffect(() => {
    const updateStageDimensions = () => {
      setStageDimensions(getStageDimensions(containerRef))
    }
    updateStageDimensions()
    window.addEventListener('resize', updateStageDimensions)
    return () => window.removeEventListener('resize', updateStageDimensions)
  }, [setStageDimensions])

  // hide/clear context menu when current frame changes
  useEffect(() => {
    clearContextMenu()
  }, [currentMonitorFrame, clearContextMenu])

  // Compute which rectangles overlap each other
  // Returns a Map: interactiveAreaId -> array of overlapping interactiveAreaIds
  const overlappingRectsMap = useMemo(() => {
    const map: Record<number, number[]> = {}

    for (let i = 0; i < rectanglesForCurrentFrame.length; i++) {
      const a = rectanglesForCurrentFrame[i]

      for (let j = i + 1; j < rectanglesForCurrentFrame.length; j++) {
        const b = rectanglesForCurrentFrame[j]

        const isOverlapping = areRectsOverlapping(a, b)

        if (isOverlapping) {
          if (!map[a.interactiveAreaId]) map[a.interactiveAreaId] = []
          if (!map[b.interactiveAreaId]) map[b.interactiveAreaId] = []
          map[a.interactiveAreaId].push(b.interactiveAreaId)
          map[b.interactiveAreaId].push(a.interactiveAreaId)
        }
      }
    }

    return map
  }, [rectanglesForCurrentFrame])

  // Compute sequential overlap order within each connected overlapping group
  // e.g. if orders 1,3,4 overlap -> 1->1, 3->2, 4->3
  const overlapOrderMap = useMemo(() => {
    const orderMap: Record<number, number> = {}
    const visited = new Set<number>()

    // Build a lookup: interactiveAreaId -> order
    const orderByIAId: Record<number, number> = {}
    rectanglesForCurrentFrame.forEach((r, index) => {
      orderByIAId[r.interactiveAreaId] = rectanglesForCurrentFrame.length - index
    })

    // Find connected components using BFS
    for (const rect of rectanglesForCurrentFrame) {
      const id = rect.interactiveAreaId
      if (visited.has(id) || !overlappingRectsMap[id]) continue

      // BFS to collect all members of this overlapping group
      const group: number[] = []
      const queue: number[] = [id]
      while (queue.length > 0) {
        const current = queue.shift()!
        if (visited.has(current)) continue
        visited.add(current)
        group.push(current)
        const neighbors = overlappingRectsMap[current] || []
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) queue.push(neighbor)
        }
      }

      // Sort group members by order (ascending) and assign sequential rank
      group.sort((a, b) => orderByIAId[a] - orderByIAId[b])
      group.forEach((iaId, idx) => {
        orderMap[iaId] = idx + 1
      })
    }

    return orderMap
  }, [rectanglesForCurrentFrame, overlappingRectsMap])

  // Stage handlers
  const onContentMousemove = data => {
    if (!isMouseDown) return

    const { left: x, top: y } = getCursorPosition(data)

    setRectangle(r => ({
      ...r,
      width: x - r.x,
      height: y - r.y
    }))
  }
  const onContentMouseup = () => {
    isMouseDown = false
    if (!rectangle) return

    const validatedRect = validateRect(rectangle)

    validatedRect && createIA(validatedRect)
    setRectangle(null)
  }
  const onMouseDown = data => {
    if (!clickedOnEmpty(data)) return
    if (isRightBttnClicked(data)) {
      onStageRightClick(data)
    } else {
      clearContextMenu()
      setSelectedInteractiveAreaIds([])
      const { left: x, top: y } = getCursorPosition(data)
      isMouseDown = true
      setRectangle({ x, y, width: 1, height: 1 })
    }
  }

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onContextMenu={e => e.preventDefault()}
    >
      {mounted &&
        <Stage
          ref={stageRef}
          width={stageWidth}
          height={stageHeight}
          // className={styles.stage}
          // onClick={handleClick}
          // onDragStart={handleDragStart}
          // onDragEnd={handleDragEnd}
          // onDragMove={e => console.log('onDragMove', e)}
          // onContentMousedown={onContentMousedown}
          onContentMousemove={onContentMousemove}
          // onContent={handleMouseUp}
          onContentMouseup={onContentMouseup}
          onMouseDown={onMouseDown}
        >
          <Layer>
            {rectanglesForCurrentFrame.map((rect: Rect) =>
              <Rectangle
                key={rect.interactiveAreaId}
                {...rect}
                zIndex={rect.zIndex}
                overlapOrder={overlapOrderMap[rect.interactiveAreaId]}
                isSelected={isIaSelected(rect.interactiveAreaId)}
                overlappingIds={overlappingRectsMap[rect.interactiveAreaId] || []}
                onSelect={data => onRectangleSelect(data, rect.interactiveAreaId)}
                onChange={newAttrs => changeRectangle(newAttrs, rect.interactiveAreaId)}
              />
            )}

            {rectangle &&
              <Rectangle
                {...rectangle}
              />
            }
          </Layer>
        </Stage>
      }

      {contextMenu &&
        <ContextMenu
          key={contextMenu.position.top + contextMenu.position.left}
          {...contextMenu}
          hide={clearContextMenu}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
        />
      }

    </div>
  )
}

export default inject(
  EDITOR_MONITOR_STORE,
  EDITOR_TIMELINE_STORE,
  EDITOR_CONTEXT_MENU_STORE,
  EDITOR_INTERACTIVE_AREAS_STORE
)(observer(InteractiveAreaCanvas))
